import React, { useEffect, useState } from 'react';
import api from './api';
import UserModal from './UserModal';
import ResetPasswordModal from './ResetPasswordModal';
import { CAN_RESET_PASSWORD_ROLES, formatDate } from './helpers';

const ROLE_LABELS = { Director: 'Directeur', Project_Lead: 'Chef de projet', Manager: 'Manager', Engineer: 'Ingénieur' };

function AccessManagement({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState(undefined); // undefined = fermé, null = nouveau
  const [resetUser, setResetUser] = useState(null);

  // Un directeur administre les comptes ; un chef de projet peut uniquement
  // dépanner ses collègues en réinitialisant un mot de passe.
  const estDirecteur = currentUser?.role === 'Director';
  const peutReinitialiser = CAN_RESET_PASSWORD_ROLES.includes(currentUser?.role);

  const load = () => {
    setLoading(true);
    api.get('/users').then((res) => setUsers(res.data)).catch(() => setError('Impossible de charger les utilisateurs.')).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleToggleActive = async (u) => {
    try {
      if (u.active) {
        await api.delete(`/users/${u.id}`);
      } else {
        await api.put(`/users/${u.id}`, { active: true });
      }
      load();
    } catch (err) {
      setError("Impossible de mettre à jour le compte.");
    }
  };

  const handleSaved = () => {
    setEditingUser(undefined);
    load();
  };

  const handleDeleteProfile = async (u) => {
    if (!window.confirm(`Supprimer définitivement le profil de ${u.username} ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/users/${u.id}/permanent`);
      load();
    } catch (err) {
      setError('Impossible de supprimer ce profil.');
    }
  };

  if (loading) return <p>Chargement...</p>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="fade-in">
      <div className="page-header-row">
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', maxWidth: 640 }}>
          Chaque utilisateur change son propre mot de passe depuis son profil (clic sur son nom, en bas à gauche).
          {peutReinitialiser && ' En cas d’oubli, vous pouvez en attribuer un nouveau sans connaître l’ancien.'}
        </p>
        {estDirecteur && (
          <button className="btn btn-primary" onClick={() => setEditingUser(null)}>+ Nouvel utilisateur</button>
        )}
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Identifiant</th>
              <th>Email</th>
              <th>Rôle</th>
              <th>Département</th>
              <th>Statut</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="avatar">{u.username.substring(0, 2).toUpperCase()}</div>
                    <div>
                      {u.username}
                      {u.password_reset_at && (
                        <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                          mdp réinitialisé le {formatDate(u.password_reset_at)}
                          {u.password_reset_by ? ` par ${u.password_reset_by}` : ''}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td>{u.email}</td>
                <td>{ROLE_LABELS[u.role] || u.role}</td>
                <td>{u.department}</td>
                <td>
                  <span className={`badge ${u.active ? 'badge-active' : 'badge-blocked'}`}>
                    {u.active ? 'Actif' : 'Désactivé'}
                  </span>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {peutReinitialiser && (
                    <>
                      <button className="btn btn-sm" onClick={() => setResetUser(u)}>
                        Réinitialiser le mot de passe
                      </button>{' '}
                    </>
                  )}
                  {estDirecteur && (
                    <>
                      <button className="btn btn-sm" onClick={() => setEditingUser(u)}>Modifier</button>{' '}
                      <button className={`btn btn-sm ${u.active ? 'btn-danger' : ''}`} onClick={() => handleToggleActive(u)}>
                        {u.active ? 'Désactiver' : 'Réactiver'}
                      </button>{' '}
                      <button className="btn btn-sm btn-danger" onClick={() => handleDeleteProfile(u)}>Supprimer</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editingUser !== undefined && (
        <UserModal user={editingUser} onClose={() => setEditingUser(undefined)} onSaved={handleSaved} />
      )}

      {resetUser && (
        <ResetPasswordModal
          user={resetUser}
          onClose={() => { setResetUser(null); load(); }}
        />
      )}
    </div>
  );
}

export default AccessManagement;
